import { Box, Flex, Heading, Text } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';
import * as m from 'motion/react-m';

import { getResizedImageUrl } from '#features/ui/components/Image/loader';
import { envConfig } from '#constants/envConfig';

// Real LFG banners generated by our Discord bot. Source PNGs are 798×392 (~55-70 KiB each);
// serve resized webp via imgproxy instead of the raw files. Rendered at ≤442 CSS px
// (85% of the 520px fan container), so 520w covers 1x and the native 798w covers retina.
// The sources live only on prod media, so without imgproxy configured the loader hands
// back the raw URL — which is exactly what we want here.
const BANNER_WIDTH = 798;
const BANNER_HEIGHT = 392;

const resized = (url: string, width: number) =>
  envConfig.IMGPROXY_ENABLED ? getResizedImageUrl(url, width, {}, 'webp') : url;

const BANNERS = [
  'https://media.teamplay.gg/assets/FdaB/xsY.png',
  'https://media.teamplay.gg/assets/FdaB/BiJ.png',
  'https://media.teamplay.gg/assets/FdaB/zt0.png',
].map((url) => ({
  src: resized(url, 520),
  srcSet: `${resized(url, 520)} 1x, ${resized(url, BANNER_WIDTH)} 2x`,
}));

// Same fanned-cards animation as the Discord bot landing (/discord-lfg-bot): each card is pinned at
// a fixed fanned position (translate + rotate on the wrapper) and only fades +
// scales in/out, staggered over a 6s loop. A blue glow pulses behind, as on the bot.
const CARDS = [
  { src: BANNERS[0], x: '5%', y: '20%', rotate: '-12deg', z: 1, times: [0, 0.08, 0.75, 0.85, 1], opacity: [0, 1, 1, 0, 0], scale: [0.85, 1, 1, 0.9, 0.9] },
  { src: BANNERS[1], x: '10%', y: '8%', rotate: '-2deg', z: 2, times: [0, 0.05, 0.15, 0.75, 0.85, 1], opacity: [0, 0, 1, 1, 0, 0], scale: [0.85, 0.85, 1, 1, 0.9, 0.9] },
  { src: BANNERS[2], x: '18%', y: '-5%', rotate: '8deg', z: 3, times: [0, 0.12, 0.22, 0.75, 0.85, 1], opacity: [0, 0, 1, 1, 0, 0], scale: [0.85, 0.85, 1, 1, 0.9, 0.9] },
];

const Fan = () => (
  <Box position="relative" w="100%" maxW="520px" mx="auto" style={{ aspectRatio: '4 / 3' }}>
    {/* Pulsing glow behind the cards */}
    <Box position="absolute" top="50%" left="50%" w="120%" h="120%" ml="-60%" mt="-60%" pointerEvents="none" asChild>
      <m.div
        style={{
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(88,101,242,0.25) 0%, transparent 60%)',
          filter: 'blur(60px)',
        }}
        animate={{ opacity: [0.6, 1, 0.6], scale: [0.95, 1.05, 0.95] }}
        transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
      />
    </Box>

    {CARDS.map((c) => (
      <Box
        key={c.src.src}
        position="absolute"
        top="16%"
        left="0"
        w="85%"
        zIndex={c.z}
        style={{ transform: `translate(${c.x}, ${c.y}) rotate(${c.rotate})` }}
      >
        <m.img
          src={c.src.src}
          srcSet={c.src.srcSet}
          width={BANNER_WIDTH}
          height={BANNER_HEIGHT}
          loading="lazy"
          decoding="async"
          alt="LFG banner"
          style={{
            display: 'block',
            width: '100%',
            borderRadius: '20px',
            boxShadow: '0 30px 60px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.1)',
          }}
          animate={{ opacity: c.opacity, scale: c.scale }}
          transition={{ duration: 6, times: c.times, repeat: Infinity, ease: 'easeInOut' }}
        />
      </Box>
    ))}
  </Box>
);

export const LfgBanners = () => {
  const { t } = useLingui();

  return (
    <Box as="section" aria-label="LFG banners" w="100%">
      <Flex direction={{ base: 'column', lg: 'row' }} alignItems="center" gap={{ base: 10, lg: 14 }}>
        <Box flex="1" w="100%" textAlign={{ base: 'center', lg: 'left' }}>
          <Text
            fontSize="xs"
            fontWeight="700"
            color="purple.300"
            textTransform="uppercase"
            letterSpacing="0.15em"
            mb={3}
          >
            {t`v3.lfgBanners.label`}
          </Text>
          <Heading
            fontWeight="900"
            fontSize={{ base: '2xl', md: '4xl' }}
            lineHeight="1.1"
            as="h2"
            maxW="480px"
            mx={{ base: 'auto', lg: 0 }}
            textTransform="uppercase"
            letterSpacing="-0.02em"
          >
            {t`v3.lfgBanners.title`}
          </Heading>
          <Text fontSize="md" color="fg.muted" lineHeight="1.7" maxW="460px" mx={{ base: 'auto', lg: 0 }} mt={4}>
            {t`v3.lfgBanners.subtitle`}
          </Text>
        </Box>

        <Box flex="1" w="100%" py={{ base: 4, md: 8 }}>
          <Fan />
        </Box>
      </Flex>
    </Box>
  );
};
